import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dedicated admin axios instance for client-type endpoints (cookie-auth).
const adminApi = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const INITIAL_NEW_TYPE = {
  name: '',
  code: '',
  description: '',
  has_guaranteed_returns: false,
  default_discount_percent: 0,
  adjustment_window_days: 7,
  is_active: true,
};

// Owns all state + handlers for the Client Types & Pricing page.
export const useClientTypes = () => {
  const [clientTypes, setClientTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newType, setNewType] = useState(INITIAL_NEW_TYPE);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [typesRes, catsRes, clientsRes] = await Promise.all([
        adminApi.get('/client-types'),
        adminApi.get('/client-types/categories'),
        adminApi.get('/admin/clients'),
      ]);
      setClientTypes(typesRes.data);
      setCategories(catsRes.data.categories || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load client types');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = async () => {
    try {
      // Generate code from name if not provided
      const code = newType.code || newType.name.toLowerCase().replace(/\s+/g, '_');
      await adminApi.post('/client-types', { ...newType, code });
      toast.success('Client type created successfully');
      setShowCreateDialog(false);
      setNewType(INITIAL_NEW_TYPE);
      loadData();
    } catch (error) {
      console.error('Error creating client type:', error);
      toast.error(error.response?.data?.detail || 'Failed to create client type');
    }
  };

  const handleUpdateType = async (typeId, updates) => {
    try {
      await adminApi.put(`/client-types/${typeId}`, updates);
      toast.success('Client type updated');
      setEditingType(null);
      loadData();
    } catch (error) {
      console.error('Error updating client type:', error);
      toast.error('Failed to update client type');
    }
  };

  const handleUpdateCategoryDiscount = async (typeId, category, discount) => {
    try {
      await adminApi.put(`/client-types/${typeId}/category-discounts`, {
        [category]: parseFloat(discount),
      });
      toast.success(`Updated ${category} discount`);
      loadData();
    } catch (error) {
      console.error('Error updating category discount:', error);
      toast.error('Failed to update discount');
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this client type?')) return;
    try {
      await adminApi.delete(`/client-types/${typeId}`);
      toast.success('Client type deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting client type:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete client type');
    }
  };

  const handleAssignClientType = async (clientId, typeId) => {
    try {
      await adminApi.put(`/client-types/assign/${clientId}?client_type_id=${typeId}`);
      toast.success('Client type assigned successfully');
      setShowAssignDialog(false);
      setSelectedClient(null);
      loadData();
    } catch (error) {
      console.error('Error assigning client type:', error);
      toast.error('Failed to assign client type');
    }
  };

  const getClientTypeName = (clientTypeId) => {
    const type = clientTypes.find((t) => t.id === clientTypeId);
    return type?.name || 'Not Assigned';
  };

  const openAssign = (client) => {
    setSelectedClient(client);
    setShowAssignDialog(true);
  };

  return {
    clientTypes,
    categories,
    clients,
    loading,
    editingType,
    setEditingType,
    showCreateDialog,
    setShowCreateDialog,
    showAssignDialog,
    setShowAssignDialog,
    selectedClient,
    newType,
    setNewType,
    handleCreateType,
    handleUpdateType,
    handleUpdateCategoryDiscount,
    handleDeleteType,
    handleAssignClientType,
    getClientTypeName,
    openAssign,
  };
};
